import React, { useEffect, useMemo, useState } from 'react';
import AddressPickerModal from './AddressPickerModal';

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="mb-1 block text-sm font-medium text-gray-700">
      {label} {required && <span className="text-red-500">*</span>}
    </span>
    {children}
  </label>
);

export default function BranchFormModal({
  open,
  onClose,
  onSubmit,
  initial = null,
  mode = 'create', // 'create' | 'edit' | 'view'
}) {
  const isCreate = mode === 'create';
  const isEdit   = mode === 'edit';
  const isView   = mode === 'view';

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    zip_code: '',
    address: '',
    lat: '',
    lng: '',
    status: 1,
  });
  const [addrOpen, setAddrOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        name: initial.name || '',
        email: initial.email || '',
        phone: initial.phone || '',
        city: initial.city || '',
        state: initial.state || '',
        zip_code: initial.zip_code || '',
        address: initial.address || '',
        lat: initial.lat || '',
        lng: initial.lng || '',
        status: typeof initial.status !== 'undefined' ? Number(initial.status) : 1,
      });
    } else {
      setForm((f) => ({ ...f, status: 1 }));
    }
  }, [open, initial]);

  const canSave = useMemo(() => {
    if (isView) return false;
    return (
      form.name.trim() &&
      form.city.trim() &&
      form.state.trim() &&
      form.address.trim()
    );
  }, [form, isView]);

  const handle = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = (e) => {
    e?.preventDefault?.();
    if (!canSave) return;
    onSubmit?.(form);
  };

  if (!open) return null;

  // Title per mode
  const title = isCreate ? 'Add Branch' : isEdit ? 'Edit Branch' : 'Branch Details';

  // shared input class: subtle, roomy, like your mock
  const inputCls =
    'db-field w-full rounded-lg border border-gray-200 px-3 py-2 text-gray-800 placeholder:text-gray-400 disabled:bg-gray-50 disabled:text-gray-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-5xl rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <i className="fa-regular fa-circle-xmark text-xl" />
          </button>
        </div>

        {/* Form body – 2 columns like your mock, airy spacing */}
        <form onSubmit={submit}>
          <div className="grid grid-cols-1 gap-x-8 gap-y-5 px-6 py-6 md:grid-cols-2">
            {/* Name */}
            <Field label="Name" required>
              <input
                className={inputCls}
                value={form.name}
                onChange={(e) => handle('name', e.target.value)}
                placeholder="Branch name"
                required
                disabled={isView}
              />
            </Field>

            {/* Lat/Lng + map button */}
            <Field label="Latitude/Longitude">
              <div className="flex gap-2">
                <input
                  className={inputCls}
                  value={form.lat}
                  onChange={(e) => handle('lat', e.target.value)}
                  placeholder="Lat"
                  disabled={isView}
                />
                <input
                  className={inputCls}
                  value={form.lng}
                  onChange={(e) => handle('lng', e.target.value)}
                  placeholder="Lng"
                  disabled={isView}
                />
                <button
                  type="button"
                  onClick={() => setAddrOpen(true)}
                  className="inline-flex items-center justify-center rounded-lg border border-gray-200 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  title="Pick on map"
                  disabled={isView}
                >
                  <i className="fa-solid fa-map-location-dot" />
                </button>
              </div>
            </Field>

            {/* Email */}
            <Field label="Email">
              <input
                className={inputCls}
                type="email"
                value={form.email}
                onChange={(e) => handle('email', e.target.value)}
                placeholder="email@example.com"
                disabled={isView}
              />
            </Field>

            {/* Phone */}
            <Field label="Phone">
              <input
                className={inputCls}
                value={form.phone}
                onChange={(e) => handle('phone', e.target.value)}
                placeholder="+1 555-555-5555"
                disabled={isView}
              />
            </Field>

            {/* City */}
            <Field label="City" required>
              <input
                className={inputCls}
                value={form.city}
                onChange={(e) => handle('city', e.target.value)}
                required
                disabled={isView}
              />
            </Field>

            {/* State */}
            <Field label="State" required>
              <input
                className={inputCls}
                value={form.state}
                onChange={(e) => handle('state', e.target.value)}
                required
                disabled={isView}
              />
            </Field>

            {/* Zip */}
            <Field label="Zip Code">
              <input
                className={inputCls}
                value={form.zip_code}
                onChange={(e) => handle('zip_code', e.target.value)}
                disabled={isView}
              />
            </Field>

            {/* Status */}
            <Field label="Status" required>
              <div className="flex items-center gap-6 rounded-lg border border-gray-200 p-2">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="status"
                    className="accent-primary"
                    checked={Number(form.status) === 1}
                    onChange={() => handle('status', 1)}
                    disabled={isView}
                  />
                  Active
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="status"
                    className="accent-primary"
                    checked={Number(form.status) === 0}
                    onChange={() => handle('status', 0)}
                    disabled={isView}
                  />
                  Inactive
                </label>
              </div>
            </Field>

            {/* Address – full width */}
            <div className="md:col-span-2">
              <Field label="Address" required>
                <textarea
                  rows={4}
                  className={inputCls}
                  value={form.address}
                  onChange={(e) => handle('address', e.target.value)}
                  placeholder=""
                  required
                  disabled={isView}
                />
              </Field>
            </div>
          </div>

          {/* Footer actions – Close + Save (hide Save in view mode) */}
          <div className="flex items-center justify-end gap-3 border-t px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="db-btn inline-flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200"
            >
              <i className="fa-regular fa-circle-xmark" />
              Close
            </button>

            {!isView && (
              <button
                type="submit"
                disabled={!canSave}
                className="db-btn inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-white shadow-[0px_6px_10px_rgba(23,_114,_255,_0.24)] disabled:opacity-50"
              >
                <i className="fa-regular fa-floppy-disk" />
                {isEdit ? 'Save Changes' : 'Save'}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Address / map picker (degrades gracefully to manual lat/lng) */}
      <AddressPickerModal
        open={addrOpen}
        onClose={() => setAddrOpen(false)}
        lat={form.lat}
        lng={form.lng}
        address={form.address}
        onPick={({ lat, lng, address }) => {
          if (lat) handle('lat', lat);
          if (lng) handle('lng', lng);
          if (address) handle('address', address);
          setAddrOpen(false);
        }}
      />
    </div>
  );
}
